import { useCameraPermissions } from 'expo-camera';
import { useCallback, useRef, useState } from 'react';
import { Vibration } from 'react-native';

const DEFAULT_THROTTLE_MS = 1500;
const VIBRATION_PATTERN = [10];

const normalizeBarcode = (value) => {
	if (!value) {
		return '';
	}

	return String(value).trim();
};

const validateEANChecksum = (barcode) => {
	const trimmed = normalizeBarcode(barcode);

	if (!trimmed) {
		return false;
	}

	const checkDigit = parseInt(trimmed[trimmed.length - 1], 10);
	if (Number.isNaN(checkDigit)) {
		return false;
	}

	let sum = 0;
	const digits = trimmed.slice(0, -1).split('').reverse();

	digits.forEach((digit, index) => {
		sum += parseInt(digit, 10) * (index % 2 === 0 ? 3 : 1);
	});

	const calculatedCheckDigit = (10 - (sum % 10)) % 10;

	return checkDigit === calculatedCheckDigit;
};

const isValidBarcode = (barcode) => {
	const code = normalizeBarcode(barcode);

	if (!code) {
		return false;
	}

	const isValidLength = code.length === 13 || code.length === 12 || code.length === 8;
	const isNumeric = /^\d+$/.test(code);

	if (!isValidLength || !isNumeric) {
		return false;
	}

	// Convert UPC-A to EAN-13 before checksum validation
	const valueToValidate = code.length === 12 ? `0${code}` : code;
	return validateEANChecksum(valueToValidate);
};

const useBarcodeScanner = ({
	onScanned,
	validateEAN = true,
	throttleMs = DEFAULT_THROTTLE_MS,
	vibrateOnScan = true,
} = {}) => {
	const [cameraPermission, requestCameraPermission] = useCameraPermissions();
	const [isScanning, setIsScanning] = useState(false);
	const lastScannedValueRef = useRef(null);
	const lastScanTimestampRef = useRef(0);

	const resetScanner = useCallback(() => {
		lastScannedValueRef.current = null;
		lastScanTimestampRef.current = 0;
	}, []);

	const handleBarcodeScanned = useCallback(async ({ data }) => {
		const barcode = normalizeBarcode(data);

		if (!barcode || isScanning) {
			return;
		}

		if (validateEAN && !isValidBarcode(barcode)) {
			return;
		}

		const now = Date.now();
		const isDuplicateScan = barcode === lastScannedValueRef.current &&
			(now - lastScanTimestampRef.current) < throttleMs;

		if (isDuplicateScan) {
			return;
		}

		lastScannedValueRef.current = barcode;
		lastScanTimestampRef.current = now;

		setIsScanning(true);

		try {
			if (vibrateOnScan) {
				Vibration.vibrate(VIBRATION_PATTERN, false);
			}

			if (onScanned) {
				await onScanned(barcode);
			}
		}
		finally {
			setIsScanning(false);
		}
	}, [isScanning, onScanned, throttleMs, validateEAN, vibrateOnScan]);

	return {
		cameraPermission,
		requestCameraPermission,
		handleBarcodeScanned,
		isScanning,
		resetScanner,
	};
};

export default useBarcodeScanner;
